import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { toOptionIdentity, toOptionLabel, toOptionValues, useGroupedOptions } from './autocompleteHelpers';

/**
 * The MUI Autocomplete behind every react-widgets Multiselect this app had. The five renderers
 * that wrapped that widget — two in FormFields, one each in FinalForm and Criteria, and the
 * creatable one — all keep their own prop names and hand them here, so no call site changes.
 *
 * Contract notes, in the order they bit:
 *
 * - The value is always an array. redux-form holds '' for an empty field, which MUI would treat
 *   as one selected option and render as a stray chip.
 * - react-widgets hid options that were already picked; `filterSelectedOptions` is that.
 * - `allowCreate` showed a "create this" row and reported the typed string. MUI's `freeSolo`
 *   appends that string to the value array, so a caller wanting objects maps it in its onChange.
 * - Grouping needs adapting twice over: react-widgets' `groupBy` could return anything (both
 *   callers here return 0 or 1) and its `renderListGroup` turned that into a heading, whereas MUI
 *   groups by a string and needs the options pre-sorted or it repeats the headings.
 */
/**
 * @param {{
 *   id?: string,
 *   name?: string,
 *   options?: any[],
 *   value?: any,
 *   onChange?: (selected: any[]) => void,
 *   onBlur?: () => void,
 *   textField?: string | ((option: any) => any),
 *   dataKey?: string,
 *   placeholder?: string,
 *   disabled?: boolean,
 *   busy?: boolean,
 *   allowCreate?: boolean,
 *   groupBy?: string | ((option: any) => any),
 *   renderListGroup?: (params: { group: any }) => React.ReactNode,
 *   error?: boolean,
 *   sx?: object,
 * }} props
 */
export default function MultiSelectAutocomplete({
  id,
  name,
  options,
  value,
  onChange,
  onBlur,
  textField,
  dataKey,
  placeholder,
  disabled,
  busy = false,
  allowCreate = false,
  groupBy,
  renderListGroup,
  error,
  sx,
}) {
  const getLabel = toOptionLabel(textField);
  const grouping = useGroupedOptions(options, groupBy, renderListGroup);
  // Some callers store the key rather than the option; resolve those back so MUI can match them.
  const selected = toOptionValues(grouping.options, dataKey, Array.isArray(value) ? value : []);

  return (
    <>
      <Autocomplete
        multiple
        autoHighlight
        filterSelectedOptions
        freeSolo={allowCreate}
        id={id}
        {...grouping}
        value={selected}
        disabled={disabled}
        loading={busy}
        getOptionLabel={getLabel}
        {...toOptionIdentity(dataKey)}
        sx={sx}
        renderValue={(selected, getItemProps) =>
          selected.map((option, index) => (
            <Chip size='small' label={getLabel(option)} {...getItemProps({ index })} key={index} />
          ))
        }
        onChange={(_event, selected) => onChange(selected)}
        // Called the way each caller called it: some pass the value back, some pass nothing.
        onBlur={onBlur}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            placeholder={placeholder}
            error={error}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {busy && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </>
  );
}
